/**
 * The application store (spec §1.6, §2, §4, §7, §8). One singleton (`store`) composed of
 * feature modules; the most-used module methods are re-exported flat so components call
 * `store.act(...)`, `store.setSearch(...)`, `store.showToast(...)` exactly as before.
 */
import { api, ApiError, groups, setUnauthorizedHandler } from '$lib/api/client';
import { db, kvGet, kvSet, openDb, rowKey, wipeAll } from '$lib/db';
import { startPruneTimer, stopPruneTimer } from '$lib/db/policy';
import type { I18nKey } from '$lib/i18n';
import { archivePath, junkPath, sentPath, snoozePath, trashPath } from '$lib/mail/folders';
import type { ComposeDraft, MessageRow, OpName, Screen, ThreadRow } from '$lib/types';
import { FoldersModule } from './folders.svelte';
import { LiveModule } from './live.svelte';
import { ListModule, type SyncOptions } from './list.svelte';
import { OpsModule, type ActOptions } from './ops.svelte';
import { PrefsModule } from './prefs.svelte';
import { PushModule } from './push.svelte';
import { SearchModule } from './search.svelte';
import { MailState } from './state.svelte';
import { ToastModule } from './ui.svelte';

export const LIVE_CHECK_DELAY_MS = 3000;

export class MailStore extends MailState {
  readonly list = new ListModule(this);
  readonly ops = new OpsModule(this);
  readonly search = new SearchModule(this);
  readonly push = new PushModule(this);
  readonly prefs = new PrefsModule(this);
  readonly live = new LiveModule(this);
  readonly foldersModule = new FoldersModule(this);
  readonly toastModule = new ToastModule(this);

  private seenTimer: ReturnType<typeof setTimeout> | undefined;
  private listenersBound = false;

  /** List rows (threads when `threadView`, plain rows while searching). */
  readonly rows: ThreadRow[] = $derived.by(() => this.list.computeRows());

  // ---- boot / session -----------------------------------------------------------------------

  /** Cache-first boot (spec §1.6): render from IndexedDB, then connect live, then sync. */
  async boot(): Promise<void> {
    this.bindListeners();
    this.ops.revive();
    await openDb();
    // The legacy one-shot purge of search-only rows lives in the v3 → v4 schema upgrade
    // (db/index.ts), which is the only place that can tell a legacy database from a fresh one.
    // It used to run here, keyed on a kv flag written at boot: on a database created by the
    // login that had just filled it, the flag was absent, so the very next boot wiped the cache
    // and every second visit started cold. Never reintroduce a runtime cache purge here.
    const user = await kvGet('user');
    if (!user) {
      this.screen = { name: 'login' };
      this.booted = true;
      return;
    }
    this.user = user;
    // A returning administrator who still has no second factor gets the same confinement the
    // server applies, rather than a shell whose every request answers 403.
    try {
      const me = await api.me();
      if (me.mustEnroll) {
        this.screen = { name: 'enroll' };
        this.booted = true;
        return;
      }
    } catch {
      /* offline or expired: carry on from cache, the usual paths handle both */
    }
    this.folders = await db.folders.toArray();
    await this.loadListFromCache(this.currentFolder);
    await this.applyHash();
    this.booted = true;
    this.logBootTiming();
    this.afterLogin();
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) this.refreshNotifyPerm();
    });
    startPruneTimer();
    await this.refresh();
  }

  /** The one console line the app prints: how long the cache-first boot took (spec §1.6). */
  private logBootTiming(): void {
    try {
      const m = performance.measure('tik-boot', 'tik-boot-start');
      console.info(`⚡ boot-from-cache: ${Math.round(m.duration)}ms`);
    } catch {
      /* `main.ts` did not set the start mark (tests, embedded use) */
    }
  }

  private bindListeners(): void {
    if (this.listenersBound || typeof window === 'undefined') return;
    this.listenersBound = true;
    window.addEventListener('online', () => {
      this.offline = false;
      void this.flushOps();
      void this.refresh();
    });
    window.addEventListener('offline', () => {
      this.offline = true;
    });
    window.addEventListener('hashchange', () => {
      if (this.syncingHash) {
        this.syncingHash = false;
        return;
      }
      void this.applyHash();
    });
    setUnauthorizedHandler(() => {
      // Session expired: show the login screen; cached data stays until logout.
      if (this.user) this.screen = { name: 'login' };
    });
  }

  /** Everything that starts once a session exists (shared by boot and login). */
  private afterLogin(): void {
    this.connectLive();
    void this.prefs.loadLabels();
    void this.prefs.loadOutbox();
    void this.prefs.loadTemplates();
    void this.search.loadSearches();
    void this.prefs.loadTrusted();
    void this.search.bootFollowups();
    void this.push.bootPush();
    void this.prefs.loadMuted();
    void this.prefs.loadSplitCounts();
    setTimeout(() => void this.checkLive(), LIVE_CHECK_DELAY_MS);
  }

  /**
   * Password step. When the mailbox has two-factor enabled the bridge issues NO cookie and
   * returns a pending id instead; the caller then asks for a code and calls `login2fa()`.
   * @returns the pending id when a second factor is required, otherwise null.
   */
  async login(user: string, pass: string): Promise<string | null> {
    const r = await api.login(user.trim(), pass);
    if (r.needs2fa && r.pending) return r.pending;
    await this.afterAuthenticated(r.user, !!r.mustEnroll);
    return null;
  }

  /** Second step: exchange the pending id and the code for a session. */
  async login2fa(pending: string, code: string): Promise<{ usedRecovery: boolean; recoveryLeft: number }> {
    const r = await api.twofaLogin(pending, code.trim());
    await this.afterAuthenticated(r.user, !!r.mustEnroll);
    return { usedRecovery: !!r.usedRecovery, recoveryLeft: r.recoveryLeft ?? 0 };
  }

  private async afterAuthenticated(user: string, mustEnroll = false): Promise<void> {
    const r = { user };
    if ((await kvGet('user')) !== r.user) await wipeAll();
    await kvSet('user', r.user);
    this.user = r.user;
    // The server confines an administrator without a second factor to enrolment, so there is
    // nothing to load yet: show the enrolment screen instead of a shell full of 403s.
    if (mustEnroll) {
      this.screen = { name: 'enroll' };
      this.booted = true;
      return;
    }
    this.navigate({ name: 'list', folder: 'INBOX' });
    this.afterLogin();
    startPruneTimer();
    await this.refresh();
  }

  /** Called by the enrolment screen once the second factor exists. */
  async enrollmentDone(): Promise<void> {
    this.navigate({ name: 'list', folder: 'INBOX' });
    this.afterLogin();
    startPruneTimer();
    await this.refresh();
  }

  async logout(): Promise<void> {
    try {
      await api.logout();
    } catch {
      /* the cookie is gone either way */
    }
    // Stop every background writer before the stores are wiped, or a pending retry would
    // recreate rows for the account that just signed out.
    this.dispose();
    await wipeAll();
    this.user = null;
    this.msgs = [];
    this.folders = [];
    this.screen = { name: 'login' };
    this.syncingHash = true;
    location.hash = '#/';
  }

  /** `watcher.state auth_error`: ask the bridge whether the session still stands. */
  async checkSession(): Promise<boolean> {
    try {
      await api.me();
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) this.screen = { name: 'login' };
      return false;
    }
  }

  connectLive(): void {
    this.live.connect();
  }

  async checkLive(): Promise<void> {
    try {
      const r = await api.live();
      this.liveDown = !r.live && r.subscribers > 0;
    } catch {
      /* offline */
    }
  }

  // ---- router (spec §2) ---------------------------------------------------------------------

  hashFor(sc: Screen): string {
    switch (sc.name) {
      case 'read':
        return `#/m/${encodeURIComponent(sc.folder)}/${sc.uid}`;
      case 'list':
        return `#/f/${encodeURIComponent(sc.folder)}`;
      case 'compose':
        return '#/compose';
      case 'settings':
        return '#/settings';
      case 'contacts':
        return '#/contacts';
      case 'outbox':
        return '#/outbox';
      default:
        return '#/';
    }
  }

  /** Swap the screen inside a view transition when available, then mirror it into the hash. */
  navigate(sc: Screen): void {
    const apply = () => {
      this.screen = sc;
    };
    const doc = typeof document !== 'undefined' ? (document as Document & { startViewTransition?: (cb: () => void) => { finished?: Promise<void>; ready?: Promise<void>; updateCallbackDone?: Promise<void> } }) : null;
    if (doc?.startViewTransition && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const vt = doc.startViewTransition(apply);
      vt.finished?.catch(() => undefined);
      vt.ready?.catch(() => undefined);
      vt.updateCallbackDone?.catch(() => undefined);
    } else apply();
    const hash = this.hashFor(sc);
    if (location.hash !== hash) {
      this.syncingHash = true;
      location.hash = hash;
    }
  }

  async applyHash(): Promise<void> {
    if (!this.user) return;
    const parts = location.hash.replace(/^#\/?/, '').split('/');
    if (parts[0] === 'm' && parts[2]) {
      const folder = decodeURIComponent(parts[1] ?? '');
      const uid = Number(parts[2]);
      if (folder && Number.isFinite(uid)) {
        await this.setFolder(folder);
        this.screen = { name: 'read', folder, uid };
        this.markSeenOnOpen(folder, uid, true);
        return;
      }
    }
    if (parts[0] === 'outbox') {
      this.screen = { name: 'outbox' };
      void this.prefs.loadOutbox();
      return;
    }
    if (parts[0] === 'f' && parts[1]) {
      const folder = decodeURIComponent(parts[1]);
      await this.setFolder(folder);
      this.screen = { name: 'list', folder };
      if (!this.offline) void this.syncFolder(folder).catch(() => undefined);
      return;
    }
    if (parts[0] === 'settings') {
      this.screen = { name: 'settings' };
      return;
    }
    if (parts[0] === 'contacts') {
      this.screen = { name: 'contacts' };
      void this.prefs.loadContacts();
      return;
    }
    this.screen = { name: 'list', folder: this.currentFolder };
  }

  backToList(): void {
    this.navigate({ name: 'list', folder: this.currentFolder });
  }

  openCompose(draft?: ComposeDraft): void {
    if (this.screen.name !== 'compose') this.composeUnder = this.screen;
    this.navigate({ name: 'compose', draft });
  }

  closeCompose(): void {
    const under = this.composeUnder;
    this.composeUnder = null;
    if (under && under.name !== 'compose') this.navigate(under);
    else this.backToList();
  }

  openSettings(): void {
    this.navigate({ name: 'settings' });
    void this.prefs.loadQuota();
  }

  openContacts(): void {
    this.navigate({ name: 'contacts' });
    void this.prefs.loadContacts();
  }

  openOutbox(): void {
    this.navigate({ name: 'outbox' });
    void this.prefs.loadOutbox();
  }

  // ---- folders ------------------------------------------------------------------------------

  get trashPath(): string | null {
    return trashPath(this.folders);
  }
  get archivePath(): string | null {
    return archivePath(this.folders);
  }
  get snoozePath(): string | null {
    return snoozePath(this.folders);
  }
  get sentPath(): string | null {
    return sentPath(this.folders);
  }
  junkPath(): string | null {
    return junkPath(this.folders);
  }
  isPurge(folder = this.currentFolder): boolean {
    return folder === this.trashPath;
  }
  canEmpty(): boolean {
    const su = this.folders.find((f) => f.path === this.currentFolder)?.specialUse ?? '';
    return su === '\\Trash' || su === '\\Junk';
  }

  refreshFolders = () => this.foldersModule.refreshFolders();
  scheduleFolderCount = (ms?: number) => this.foldersModule.scheduleFolderCount(ms);
  newFolder = (name: string, parent?: string) => this.foldersModule.newFolder(name, parent);
  renameFolder = (path: string, name: string, parent?: string) => this.foldersModule.renameFolder(path, name, parent);
  deleteFolder = (path: string) => this.foldersModule.deleteFolder(path);
  markFolderRead = (folder?: string) => this.foldersModule.markFolderRead(folder);
  emptyFolder = () => this.foldersModule.emptyFolder();

  /** Flush ops, refresh folders (or defer), sync the current folder; 401 → login screen. */
  async refresh(): Promise<void> {
    if (this.syncing || this.offline || !this.user) return;
    this.syncing = true;
    try {
      await this.flushOps();
      if ((await db.ops.count()) === 0) await this.refreshFolders();
      else this.scheduleFolderCount();
      await this.syncFolder(this.currentFolder);
      void this.prefs.loadSplitCounts(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) this.screen = { name: 'login' };
    } finally {
      this.syncing = false;
    }
  }

  async retrySync(): Promise<void> {
    this.lastSyncError = null;
    try {
      await this.refreshFolders();
      await this.syncFolder(this.currentFolder);
    } catch {
      this.noteSyncError(this.currentFolder);
    }
  }

  noteSyncError(folder: string): void {
    this.lastSyncError = { folder, at: Date.now() };
  }

  async setFolder(folder: string): Promise<void> {
    if (folder !== this.currentFolder) {
      this.clearSelection();
      this.clearSearch();
      this.dismissUndo();
      this.currentFolder = folder;
      this.folderTotal = 0;
      this.folderLoaded = 0;
      this.listScrollTop = 0;
      this.cursorKey = null;
    }
    await this.loadListFromCache(folder);
    await this.list.countLoaded(folder);
  }

  async openFolder(folder: string): Promise<void> {
    await this.setFolder(folder);
    this.navigate({ name: 'list', folder });
    if (!this.offline) {
      void this.syncFolder(folder, { reconcile: true }).catch(() => undefined);
      void this.prefs.loadLabels();
    }
  }

  // ---- reading ------------------------------------------------------------------------------

  /** Drafts open in compose; anything else opens the reader and marks seen after a dwell. */
  async openMessage(uid: number, folder?: string): Promise<void> {
    const f = folder || this.currentFolder;
    if (this.folders.find((x) => x.path === f)?.specialUse === '\\Drafts') {
      try {
        const m = await api.message(f, uid);
        this.openCompose({
          to: m.to.map((a) => a.address).join(', '),
          cc: m.cc.map((a) => a.address).join(', ') || undefined,
          subject: m.subject,
          text: m.body?.type === 'text/plain' ? m.body.content : undefined,
          html: m.body?.type === 'text/html' ? m.body.content : undefined,
          draftUid: uid,
        });
      } catch {
        this.showToast('netError');
      }
      return;
    }
    this.cursorKey = rowKey(f, uid);
    this.dismissUndo();
    this.navigate({ name: 'read', folder: f, uid });
    this.markSeenOnOpen(f, uid, true);
  }

  /**
   * Release every background resource: timers, the live stream, in-flight requests and the
   * cache pruner. Idempotent. Called on logout, on page unload and by tests before the
   * database is deleted — anything still running would write into a closed Dexie handle.
   */
  dispose(): void {
    clearTimeout(this.seenTimer);
    this.seenTimer = undefined;
    this.ops.dispose();
    this.foldersModule.dispose();
    this.prefs.dispose();
    this.toastModule.dispose();
    this.live.disconnect();
    stopPruneTimer();
    groups.cancelAll();
  }

  /** Mark seen after `DWELL_MS` (1200 ms) if still on that message and it is unseen. */
  markSeenOnOpen(folder: string, uid: number, dwell: boolean): void {
    clearTimeout(this.seenTimer);
    const row = this.msgs.find((m) => m.uid === uid && m.folder === folder);
    if (row && row.flags.includes('\\Seen')) return;
    const run = () => {
      const sc = this.screen;
      if (sc.name === 'read' && sc.uid === uid && sc.folder === folder) void this.act('seen', folder, [uid], undefined, { undoable: false });
    };
    if (dwell) this.seenTimer = setTimeout(run, MailState.DWELL_MS);
    else run();
  }

  /** Split view: moving the cursor previews the row. */
  previewCursor(): void {
    const key = this.cursorKey;
    if (!key) return;
    const row = this.msgs.find((m) => m.key === key);
    if (!row) return;
    const sc = this.screen;
    if (sc.name === 'read' && sc.uid === row.uid && sc.folder === row.folder) return;
    void this.openMessage(row.uid, row.folder);
  }

  /** After a "leave" action: open the neighbour per `autoAdvance`, else back to the list. */
  afterAction(next: { uid: number; folder: string; key: string } | null): void {
    if (!next) {
      this.backToList();
      return;
    }
    this.cursorKey = next.key;
    void this.openMessage(next.uid, next.folder);
  }

  async editAsNew(folder: string, uid: number): Promise<void> {
    try {
      const m = await api.message(folder, uid);
      this.openCompose({
        to: m.to.map((a) => a.address).join(', '),
        cc: m.cc.map((a) => a.address).join(', ') || undefined,
        subject: m.subject,
        text: m.body?.type === 'text/plain' ? m.body.content : undefined,
        html: m.body?.type === 'text/html' ? m.body.content : undefined,
      });
    } catch {
      this.showToast('netError');
    }
  }

  // ---- bulk helpers over the selection ------------------------------------------------------

  async bulk(op: OpName, dest?: string): Promise<void> {
    if (this.list.staleSelection()) return;
    const uids = [...this.selection];
    if (!uids.length) return;
    this.clearSelection();
    await this.act(op, this.currentFolder, uids, dest, { undoable: op === 'delete' || op === 'unseen' || op === 'seen' });
  }

  async moveTo(dest: string, uids?: number[]): Promise<void> {
    if (!uids && this.list.staleSelection()) {
      this.moveSheet = false;
      return;
    }
    const list = uids ?? [...this.selection];
    if (!list.length) return;
    this.clearSelection();
    this.moveSheet = false;
    await this.act('move', this.currentFolder, list, dest, { undoable: true });
  }

  async remove(uids: number[], folder = this.currentFolder): Promise<void> {
    if (uids.length) await this.act('delete', folder, uids, undefined, { undoable: true });
  }

  async archive(uids: number[], folder = this.currentFolder): Promise<void> {
    const dest = this.archivePath;
    if (!dest || folder === dest || !uids.length) return;
    this.clearSelection();
    await this.act('move', folder, uids, dest, { undoable: true, toast: 'archivedToast' });
  }

  async dropOn(path: string): Promise<void> {
    const uids = this.dragUids ? [...this.dragUids] : null;
    const from = this.dragFrom;
    this.dragUids = null;
    this.dragFrom = null;
    if (!uids?.length || !path || path === from) return;
    await this.act('move', from || this.currentFolder, uids, path, { undoable: true });
    this.clearSelection();
  }

  // ---- flat delegation (the names components already use) ----------------------------------

  syncFolder = (folder: string, opts?: SyncOptions) => this.list.syncFolder(folder, opts);
  loadMore = () => this.list.loadMore();
  loadListFromCache = (folder: string) => this.list.loadListFromCache(folder);
  ensurePreviews = (uids: readonly number[]) => this.list.ensurePreviews(uids);
  threadOf = (uid: number, folder?: string) => this.list.threadOf(uid, folder);
  threadUids = (uid: number, folder?: string) => this.list.threadUids(uid, folder);
  moveCursor = (d: number) => this.list.moveCursor(d);
  cursorTo = (w: 'start' | 'end') => this.list.cursorTo(w);
  cursorRow = () => this.list.cursorRow();
  cursorUid = () => this.list.cursorUid();
  cursorUids = () => this.list.cursorUids();
  toggleRow = (row: ThreadRow) => this.list.toggleRow(row);
  toggleSelect = (uid: number) => this.list.toggleSelect(uid);
  clearSelection = () => this.list.clearSelection();
  selectOnly = (uid: number) => this.list.selectOnly(uid);
  selectAllShown = () => this.list.selectAllShown();
  selectWhere = (pred: (flags: string[]) => boolean) => this.list.selectWhere(pred);
  selectRangeTo = (row: ThreadRow) => this.list.selectRangeTo(row);
  staleSelection = () => this.list.staleSelection();
  readerPos = () => this.list.readerPos();
  nextAfter = () => this.list.nextAfter();
  openNeighbor = (d: number) => this.list.openNeighbor(d);

  act = (op: OpName, folder: string, uids: readonly number[], dest?: string, opts?: ActOptions) => this.ops.act(op, folder, uids, dest, opts);
  flushOps = () => this.ops.flushOps();
  undo = () => this.ops.undo();
  dismissUndo = () => this.ops.dismissUndo();

  setSearch = (q: string) => this.search.setSearch(q);
  runSearch = (q: string) => this.search.runSearch(q);
  clearSearch = () => this.search.clearSearch();
  retrySearch = () => this.search.retrySearch();
  rememberSearch = (q: string) => this.search.rememberSearch(q);
  forgetSearches = () => this.search.forgetSearches();
  saveSearch = (name: string) => this.search.saveSearch(name);
  removeSearch = (id: string) => this.search.removeSearch(id);
  actOnAllMatches = (op: string, extra?: Record<string, unknown>) => this.search.actOnAllMatches(op, extra);
  filterByLabel = (label: string) => this.search.filterByLabel(label);
  showFollowups = () => this.search.showFollowups();
  bootFollowups = () => this.search.bootFollowups();
  suggest = (needle: string, limit?: number) => this.search.suggest(needle, limit);
  get searchTerms(): string[] {
    return this.search.terms;
  }
  get searchLabel(): string | null {
    return this.search.searchLabel;
  }

  showToast = (key: I18nKey | string, undoable = false, ms?: number) => this.toastModule.show(key, undoable, ms);
  holdToast = () => this.toastModule.hold();
  releaseToast = () => this.toastModule.release();

  bootPush = () => this.push.bootPush();
  enableNotifications = () => this.push.enableNotifications();
  disableNotifications = () => this.push.disableNotifications();
  enablePush = () => this.push.enablePush();
  disablePush = () => this.push.disablePush();
  testPush = () => this.push.testPush();
  notifyNew = (rows: readonly MessageRow[]) => this.push.notifyNew(rows);
  applyBadge = (unseen: number) => this.push.applyBadge(unseen);

  loadLabels = () => this.prefs.loadLabels();
  setLabel = (uids: number[], label: string, on: boolean) => this.prefs.setLabel(uids, label, on);
  loadSplitCounts = (force = false) => this.prefs.loadSplitCounts(force);
  loadMuted = () => this.prefs.loadMuted();
  muteThread = (uid: number, folder?: string) => this.prefs.muteThread(uid, folder);
  muteSelected = (uids?: number[]) => this.prefs.muteSelected(uids);
  unmute = (id: string) => this.prefs.unmute(id);
  snooze = (uids: number[], until: number) => this.prefs.snooze(uids, until);
  unsnooze = (uids: number[]) => this.prefs.unsnooze(uids);
  snoozeOptions = () => this.prefs.snoozeOptions();
  scheduleOptions = () => this.prefs.scheduleOptions();
  loadOutbox = () => this.prefs.loadOutbox();
  startSendUndo = (id: string, seconds: number) => this.prefs.startSendUndo(id, seconds);
  cancelSend = () => this.prefs.cancelSend();
  cancelScheduled = (id: string) => this.prefs.cancelScheduled(id);
  loadTemplates = () => this.prefs.loadTemplates();
  saveTemplate = (name: string, body: string) => this.prefs.saveTemplate(name, body);
  removeTemplate = (id: string) => this.prefs.removeTemplate(id);
  loadTrusted = () => this.prefs.loadTrusted();
  isTrusted = (addr: string | null | undefined) => this.prefs.isTrusted(addr);
  trustSender = (addr: string) => this.prefs.trustSender(addr);
  untrustSender = (id: string) => this.prefs.untrustSender(id);
  loadContacts = () => this.prefs.loadContacts();
  addContact = (name: string, email: string) => this.prefs.addContact(name, email);
  deleteContact = (id: string) => this.prefs.deleteContact(id);
  importContacts = () => this.prefs.importContacts();
  addContactQuick = (name: string, addr: string) => this.prefs.addContactQuick(name, addr);
  loadQuota = () => this.prefs.loadQuota();
}
